import React from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SaveIcon from '@mui/icons-material/Save';
import DataObjectIcon from '@mui/icons-material/DataObject';

import BieEntryGrid from './BieEntryGrid';
import { importExcelRows } from './bie.apis';

type Props = {
  scenario: string;
  rows: any[];
  lookups?: any;
  loading?: boolean;
  onRowsChange: (rows: any[]) => void;
  onSubmitRows: (rows: any[]) => Promise<void>;
  onImported: () => void;
};

type State = {
  text: string;
  error: string;
  message: string;
  jsonOpen: boolean;
};

class ImportWizardV2 extends React.Component<Props, State> {
  state: State = {
    text: '[{"etc":"SALE","enc":"SUPER","lcc":"BINGO","pdc":"2026M01","qty":1000000,"upr":735,"amt":735000000}]',
    error: '',
    message: '',
    jsonOpen: false,
  };

  importJson = async () => {
    try {
      this.setState({ error: '', message: '' });
      const records = JSON.parse(this.state.text || '[]');
      const res: any = await importExcelRows(this.props.scenario, records);
      this.setState({ message: `Imported ${res.rc || res.sd?.rc || records.length || 0} record(s).` });
      this.props.onImported();
    } catch (e: any) {
      this.setState({ error: e.message || String(e) });
    }
  };

  handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      const text = await file.text();

      this.setState({
        text,
        message: `Loaded ${file.name}. Review JSON then import.`,
        error: '',
        jsonOpen: true,
      });
    } catch (e: any) {
      this.setState({ error: e.message || String(e) });
    }
  };

  render() {
    const lookups = this.props.lookups || {};

    return (
      <Paper elevation={1} sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6">Simulation Entries</Typography>
            <Typography variant="body2" color="text.secondary">
              Use manual entry for small edits or JSON/file import for bulk simulation parameters.
            </Typography>
          </Box>

          {this.state.error && <Alert severity="error">{this.state.error}</Alert>}
          {this.state.message && <Alert severity="success">{this.state.message}</Alert>}

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
              Load JSON File
              <input hidden type="file" accept=".json,.txt" onChange={this.handleFile} />
            </Button>
            <Button variant="outlined" startIcon={<DataObjectIcon />} onClick={() => this.setState({ jsonOpen: !this.state.jsonOpen })}>
              {this.state.jsonOpen ? 'Hide JSON Import' : 'Show JSON Import'}
            </Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={this.importJson} disabled={!this.props.scenario}>
              Import JSON
            </Button>
          </Stack>

          {this.state.jsonOpen && (
            <TextField
              label="Import records JSON"
              value={this.state.text}
              onChange={(e) => this.setState({ text: e.target.value })}
              multiline
              minRows={5}
              fullWidth
            />
          )}

          <Divider />

          <BieEntryGrid
            rows={this.props.rows}
            loading={this.props.loading}
            onChange={this.props.onRowsChange}
            onSubmit={this.props.onSubmitRows}
            entryTypes={lookups.entryTypes}
            periods={lookups.periods}
            entities={lookups.entities}
            locations={lookups.locations}
            accounts={lookups.accounts}
            drivers={lookups.drivers}
          />
        </Stack>
      </Paper>
    );
  }
}

export default ImportWizardV2;
